import { useMemo, useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
} from '../services/types';

import { Button } from './Button';

export interface PdfTableSelectionCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

interface PdfTableCandidate {
  table_index: number;
  row_count: number;
  column_count: number;
  preview_rows: string[][];
}

/**
 * v0.5 Stage G.3 — UI card for the ``pdf_table_selection`` HITL.
 *
 * Fired by ``agents/fia.py``'s PDF branch when ``blob_handler`` finds
 * more than one table in the source PDF. The reviewer picks one or
 * more candidate tables (single-select for the most common case, but
 * the multi-select path is exercised when a PDF legitimately holds
 * multiple tables that should each become independent cleansed CSVs).
 *
 * Carry-forward keys on ``file_hash`` — same-bytes re-uploads pick
 * up the prior selection without re-prompting.
 */
export function PdfTableSelectionCard({
  hit,
  onAction,
  reviewerId,
}: PdfTableSelectionCardProps) {
  const candidates = useMemo<PdfTableCandidate[]>(
    () =>
      Array.isArray(hit.candidates)
        ? (hit.candidates as unknown as PdfTableCandidate[])
        : [],
    [hit.candidates],
  );

  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(() => {
    // Default-pick the largest table (matches v0.4's auto-pick) so the
    // common single-select case is one click. Reviewer can switch /
    // multi-select before confirming.
    if (candidates.length === 0) return new Set();
    const largest = candidates.reduce((a, b) =>
      b.row_count > a.row_count ? b : a,
    );
    return new Set([largest.table_index]);
  });

  const toggle = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const onConfirm = async () => {
    setPending('choose_pdf_tables');
    setError(null);
    try {
      const indices = [...selected].sort((a, b) => a - b);
      await onAction(hit.hit_id, {
        type: 'choose_pdf_tables',
        payload: { table_indices: indices },
        reviewer_id: reviewerId,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const canConfirm = selected.size > 0 && !pending;

  return (
    <li
      data-rule-card="pdf-table-selection"
      data-resolution-type="pdf_table_selection"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            Pick PDF tables to extract
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            pdf table selection
          </span>
        </div>
        <p className="text-xs text-muted m-0">{hit.issue}</p>
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              Detected tables ({candidates.length})
            </legend>
            {candidates.map((cand) => {
              const isSelected = selected.has(cand.table_index);
              return (
                <label
                  key={cand.table_index}
                  className={`flex items-start gap-3 rounded border p-2 cursor-pointer hover:bg-surface-alt/30 ${
                    isSelected ? 'border-warn bg-surface-alt/30' : 'border-border'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={`pdf-table-${hit.hit_id}`}
                    value={cand.table_index}
                    checked={isSelected}
                    onChange={() => toggle(cand.table_index)}
                    className="mt-0.5"
                    data-testid={`pdf-table-${cand.table_index}`}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-sm">
                      Table #{cand.table_index} · {cand.row_count} rows ×{' '}
                      {cand.column_count} cols
                    </span>
                    {cand.preview_rows.length > 0 && (
                      <span className="block mt-1 overflow-x-auto">
                        <table className="text-[11px] font-mono border-collapse">
                          <tbody>
                            {cand.preview_rows.map((row, ri) => (
                              <tr key={ri}>
                                {row.map((cell, ci) => (
                                  <td
                                    key={ci}
                                    className="border border-border px-1 py-0.5 text-muted whitespace-nowrap max-w-[12ch] overflow-hidden text-ellipsis"
                                  >
                                    {cell || ' '}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </fieldset>

          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={!canConfirm}
              data-testid="pdf-tables-confirm"
            >
              {pending === 'choose_pdf_tables'
                ? 'Saving…'
                : `Confirm ${selected.size} table${selected.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
